'use client';

import React, { useEffect, useRef, useState } from 'react';

const benefits = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" />
      </svg>
    ),
    title: 'Visibilidad 24/7',
    desc: 'Tu negocio aparece en búsquedas locales todo el día, todos los días.',
    span: 'col-span-1',
    bg: 'rgba(244,141,49,0.08)',
    border: 'rgba(244,141,49,0.15)',
    color: '#F48D31',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    title: 'Métricas en tiempo real',
    desc: 'Sabe cuántas personas buscan tus productos, cuántos te contactan y más.',
    span: 'col-span-2',
    bg: 'rgba(0,201,184,0.08)',
    border: 'rgba(0,201,184,0.15)',
    color: '#00C9B8',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    title: 'Perfil verificado',
    desc: 'Genera confianza con una insignia de negocio verificado por AQUÍ APP.',
    span: 'col-span-1',
    bg: 'rgba(2,215,215,0.08)',
    border: 'rgba(2,215,215,0.15)',
    color: '#02D7D7',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    title: 'Sin comisiones ocultas',
    desc: 'Planes transparentes. Sabes exactamente lo que pagas y lo que recibes.',
    span: 'col-span-1',
    bg: 'rgba(0,201,184,0.08)',
    border: 'rgba(0,201,184,0.15)',
    color: '#00C9B8',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: 'Soporte dedicado',
    desc: 'Contamos con un equipo técnico 24/7, para garantizar la continuidad operativa de la plataforma.',
    span: 'col-span-1',
    bg: 'rgba(244,141,49,0.08)',
    border: 'rgba(244,141,49,0.15)',
    color: '#F48D31',
  },
];

export default function PartnerCTASection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [category, setCategory] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const hiddenEls = entry.target.querySelectorAll('.reveal-hidden');
            hiddenEls.forEach((el, i) => {
              setTimeout(() => el.classList.add('reveal-visible'), i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="aliados" ref={sectionRef} className="py-32 relative overflow-hidden">
      {/* Warm glow */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full blur-[150px] opacity-8"
          style={{ background: 'radial-gradient(ellipse, rgba(255,77,0,0.15) 0%, rgba(255,214,0,0.05) 60%, transparent 100%)' }}
        />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        {/* Main CTA Banner */}
        <div
          className="reveal-hidden rounded-[40px] p-10 lg:p-16 mb-16 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #F48D31 0%, #D4721A 40%, #F7A85A 100%)',
          }}
        >
          {/* Pattern overlay */}
          <div className="absolute inset-0 grid-pattern opacity-20" />

          {/* Decorative circles */}
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full border-2 border-white/10" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full border border-white/10" />

          <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
                <span className="text-white text-sm font-bold">Para partners</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-[0.9] text-white">
                ÚNETE,
                <br />
                CONECTA
                <br />
                <span className="text-accent">Y VENDE!</span>
              </h2>
              <p className="text-white/80 text-lg leading-relaxed max-w-md">
                ¿Tienes un negocio local? Registrarlo en AQUÍ APP es gratis durante el lanzamiento. Aumenta tu visibilidad y llega a miles de clientes en tu zona.
              </p>
            </div>
            <div className="space-y-5">
              {/* Registration card */}
              <div
                id="registro"
                className="rounded-[28px] p-8 space-y-6"
                style={{ background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.15)' }}
              >
                <div>
                  <h3 className="text-xl font-black text-white mb-1">¡Regístrate gratis!</h3>
                  <p className="text-white/60 text-sm">Sé de los primeros partners en lanzamiento.</p>
                </div>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Nombre de tu negocio"
                    className="w-full px-5 py-3.5 rounded-xl text-sm font-medium text-white placeholder:text-muted outline-none focus:ring-2 transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                  />
                  <input
                    type="email"
                    placeholder="Tu correo electrónico"
                    className="w-full px-5 py-3.5 rounded-xl text-sm font-medium text-white placeholder:text-muted outline-none focus:ring-2 transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                  />
                  <input
                    type="tel"
                    placeholder="Número de teléfono"
                    className="w-full px-5 py-3.5 rounded-xl text-sm font-medium text-white placeholder:text-muted outline-none focus:ring-2 transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                  />
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    aria-label="Categoría de tu negocio"
                    className={`w-full px-5 py-3.5 rounded-xl text-sm font-medium outline-none focus:ring-2 transition-all appearance-none ${
                      category === '' ? 'text-muted' : 'text-white'
                    }`}
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                  >
                    <option value="">Selecciona tu categoría</option>
                    <option value="hogar">Hogar & Ferretería</option>
                    <option value="comida">Comida & Restaurantes</option>
                    <option value="salud">Salud & Bienestar</option>
                    <option value="tech">Tecnología</option>
                    <option value="vehiculos">Vehículos & Repuestos</option>
                    <option value="moda">Moda & Accesorios</option>
                    <option value="servicios">Servicios Profesionales</option>
                    <option value="mascotas">Mascotas & Veterinarias</option>
                  </select>
                  <button
                    type="button"
                    className="w-full py-4 rounded-xl font-black text-sm uppercase tracking-widest transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{ background: '#00C9B8', color: '#000758', boxShadow: '0 6px 24px rgba(0,201,184,0.4)' }}
                  >
                    ¡Quiero ser Aliado!
                  </button>
                </div>
                <p className="text-white/40 text-xs text-center">
                  Al registrarte aceptas nuestros términos. Sin Spam.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Bento */}
        <div className="reveal-hidden mb-4">
          <p className="text-[10px] font-black uppercase tracking-[0.5em] text-muted">Beneficios para partners</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {benefits?.map((b, i) => (
            <div
              key={i}
              className={`reveal-hidden bento-card rounded-[24px] border p-7 group cursor-default ${
                b?.span === 'col-span-2' ? 'md:col-span-2' : ''
              }`}
              style={{
                background: b?.bg,
                borderColor: b?.border,
              }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
                style={{ background: `${b?.color}20`, color: b?.color }}
              >
                {b?.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{b?.title}</h3>
              <p className="text-sm text-muted leading-relaxed">{b?.desc}</p>
            </div>
          ))}
        </div>

        {/* Consumer waitlist */}
        <div
          className="reveal-hidden mt-8 rounded-[28px] p-8 border flex flex-col md:flex-row items-center justify-between gap-8"
          style={{ background: '#001070', borderColor: 'rgba(255,255,255,0.06)' }}
        >
          <div className="space-y-2">
            <p className="text-[10px] font-black uppercase tracking-widest text-primary">Para consumidores</p>
            <h3 className="text-2xl font-black text-white">¿Buscas productos cerca de ti?</h3>
            <p className="text-muted text-sm max-w-md">Regístrate para ser notificado cuando AQUÍ APP llegue a tu ciudad. ¡Próximamente en iOS y Android!</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <input
              type="email"
              placeholder="tu@correo.com"
              className="px-5 py-3.5 rounded-xl text-sm font-medium text-white placeholder-muted outline-none flex-1 md:w-64"
              style={{ background: '#1C1F2E', border: '1px solid rgba(255,255,255,0.08)' }}
            />
            <button
              type="button"
              className="px-7 py-3.5 rounded-xl font-black text-sm uppercase tracking-widest text-bg hover:scale-105 transition-all whitespace-nowrap"
              style={{ background: '#FF4D00', boxShadow: '0 4px 16px rgba(255,77,0,0.35)' }}
            >
              Notifícame
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}